/**
 * Per-feature DOM-marker style helpers for the LiveGeoJSONLayer
 * `fa-marker` style type.
 *
 * Each rule resolves at render time against the feature's properties.
 * Rules are intentionally simple — a static value, or a `{ field, zero,
 * nonzero }` triple that branches on whether `properties[field]` is
 * zero/null/falsy. Anything fancier should use mapbox expressions on
 * a regular circle/symbol layer instead.
 */

export type StaticRule<T> = T;
export type ZeroNonzeroRule<T> = { field: string; zero: T; nonzero: T };
export type StyleRule<T> = StaticRule<T> | ZeroNonzeroRule<T>;

/** Pixel offset from the feature's coordinates: [x, y], y negative is upward. */
export type MarkerOffset = [number, number];

export interface FaMarkerStyle {
  type: 'fa-marker';
  icon: string;
  color?: StyleRule<string>;
  size?: StyleRule<number>;
  hoverTemplate?: string;
  /**
   * #790 — move the marker off its own coordinates so it does not cover
   * something drawn at the same place. The rainfall droplets sit on the Live
   * Weather stations, and because the droplets are DOM markers while the
   * stations are a GL symbol layer, the droplets always paint on top and no
   * z-order change can reach them. While a value is being read, legibility
   * matters more than spatial exactness.
   *
   * Static only: an offset that changed per feature would move a marker away
   * from the thing it is describing for some features and not others.
   */
  offset?: MarkerOffset;
}

/** The gap a hover popup keeps from the thing it describes, in pixels. */
export const POPUP_GAP_PX = 14;

export interface PopupPlacement {
  offset: number | MarkerOffset;
  anchor?: 'top-left';
}

/**
 * #790 — where an fa-marker's hover popup sits.
 *
 * A popup anchored at the feature's coordinates lands on whatever else is drawn
 * there. For the rainfall layers that is the Live Weather station's own window,
 * which covered the mm value — worst for short station names, whose narrow
 * window lands mid-text.
 *
 * Two placements were measured and rejected. Carrying only the marker's offset
 * still intersected the window. Anchoring bottom-left, up and to the right, left
 * a 5px sliver — and worse, it could not generalise: the station window is
 * centred on the feature and its width follows the station NAME, so no fixed
 * horizontal gap clears a long one.
 *
 * The reliable axis is vertical. The station window is always ABOVE the feature,
 * so the popup is anchored top-left and placed below and to the right, where it
 * is clear of the window whatever the name's length.
 *
 * A layer that never asked to move keeps the plain numeric offset, and with it
 * the exact placement it had.
 */
export function resolvePopupPlacement(style: FaMarkerStyle | undefined): PopupPlacement {
  const [x, y] = resolveMarkerOffset(style);
  if (x === 0 && y === 0) return { offset: POPUP_GAP_PX };
  return { offset: [x + POPUP_GAP_PX, POPUP_GAP_PX], anchor: 'top-left' };
}

/**
 * The offset to apply, or [0, 0]. Layer style comes from the database, so a
 * malformed value is a configuration error reaching render — it is ignored
 * rather than thrown, which would take out every marker on the layer.
 */
export function resolveMarkerOffset(style: FaMarkerStyle | undefined): MarkerOffset {
  const offset = style?.offset;
  if (!Array.isArray(offset) || offset.length !== 2) return [0, 0];
  const [x, y] = offset;
  if (typeof x !== 'number' || typeof y !== 'number') return [0, 0];
  if (!Number.isFinite(x) || !Number.isFinite(y)) return [0, 0];
  return [x, y];
}

function isZeroNonzeroRule<T>(rule: StyleRule<T>): rule is ZeroNonzeroRule<T> {
  return (
    typeof rule === 'object' &&
    rule !== null &&
    'field' in rule &&
    'zero' in rule &&
    'nonzero' in rule
  );
}

export function resolveStyleValue<T>(
  rule: StyleRule<T> | undefined,
  fallback: T,
  props: Record<string, unknown>,
): T {
  if (rule === undefined || rule === null) return fallback;
  if (!isZeroNonzeroRule(rule)) return rule;
  const v = props?.[rule.field];
  const isZero = v === 0 || v === null || v === undefined || v === '' || v === false;
  return isZero ? rule.zero : rule.nonzero;
}

/**
 * Replace `{{field}}` tokens with values from properties. Numeric values
 * are formatted with up to 2 decimals (trailing zeros trimmed). Missing
 * fields render as the literal token so the template author notices.
 */
export function renderHoverTemplate(
  template: string,
  props: Record<string, unknown>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
    if (!(key in props)) return `{{${key}}}`;
    const v = props[key];
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') {
      const rounded = Math.round(v * 100) / 100;
      return String(rounded);
    }
    return String(v);
  });
}
